import { useEffect, useState } from 'react';
import { DraggableModal } from '../DraggableModal';
import { apiService } from '../../services/api';
import {
  PaymentData,
  PaymentReason,
  PaymentStatus,
  ReportsDetails,
  UserRole,
} from '../../services/api.types';
import { useAppSelector, useToast } from '../../hooks';
import { formatMoney } from '../../utils/formatters';
import dayjs from 'dayjs';
import { TeacherReport } from '../TeacherReport';
import { ReportInfo } from '../../pages/ListReports';
import { getEndHour } from '../../utils/date';
import { Edit, LaunchRounded, LocalAtm } from '@mui/icons-material';
import {
  translatePaymentMethod,
  translatePaymentReason,
  translatePaymentStatus,
} from '../../utils/translate-enums';
import { Button } from '../Button';
import CancelIcon from '@mui/icons-material/Cancel';
import { ConfirmationModal } from '../ConfirmationModal';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { StatusLabel } from '../StatusLabel';
import { CircularProgress } from '@mui/material';
import { UpdatePaymentModal } from '../UpdatePaymentModal';
import { ReportsInfoList } from '../ReportsListModal/ReportsInfoList';

interface ChargeDetailsModalProps {
  open: boolean;
  payment: PaymentData;
  onClose: () => void;
  hideReportsButons?: boolean;
  onUpdate?: (updatedPayment: PaymentData) => void;
}

export const ChargeDetailsModal = ({
  open,
  onClose,
  payment,
  hideReportsButons = true,
  onUpdate,
}: ChargeDetailsModalProps) => {
  const toast = useToast();
  const user = useAppSelector((state) => state.user);
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;

  const [reportsDetails, setReportsDetails] = useState<ReportsDetails[]>([]);
  const [isPaymentsDataLoading, setIsPaymentsDataLoading] =
    useState<boolean>(false);
  const [reportInfo, setReportInfo] = useState<ReportInfo | undefined>();
  const [reportInfoOpen, setReportInfoOpen] = useState<boolean>(false);

  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
  const [isLoadingCancel, setIsLoadingCancel] = useState<boolean>(false);
  const [isLoadingRestore, setIsLoadingRestore] = useState<boolean>(false);

  const [openReceiveInCashModal, setOpenReceiveInCashModal] =
    useState<boolean>(false);
  const [isLoadingReceiveInCash, setIsLoadingReceiveInCash] =
    useState<boolean>(false);

  const [openUpdatePaymentModal, setOpenUpdatePaymentModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (!user) return;
    setIsPaymentsDataLoading(true);
    apiService
      .getPaymentDetails(payment.id)
      .then((res) => {
        setReportsDetails(res);
      })
      .finally(() => {
        setIsPaymentsDataLoading(false);
      });
  }, [user, payment]);

  const isPaid = payment.status === PaymentStatus.PAID;
  const showCancelButton =
    isAdminOrEmployee &&
    payment.status === PaymentStatus.PENDING &&
    (payment.reason !== PaymentReason.PAY_FOR_CLASSES ||
      reportsDetails.length === 0);
  const showRestoreButton =
    isAdminOrEmployee && payment.status === PaymentStatus.CANCELED;

  const handleOpenReport = async (reportDetails: ReportsDetails) => {
    if (!reportDetails) return;
    await apiService.findReport(reportDetails.reportId).then((res) => {
      setReportInfo({
        id: res.id,
        classLocation: res.place,
        date: res.date,
        email: reportDetails.teacherEmail,
        startTimeClass: res.startHour,
        endTimeClass: getEndHour(res.startHour, res.duration),
        fullName: reportDetails.teacherName,
        studentName: reportDetails.studentName,
        responsibleFullName: reportDetails.responsibleName,
        generalObservation: res.observations,
        status: res.status,
        studentPerformance: res.studentPerformance,
        taughtContent: res.taughtContent,
        reviewRequest: res.reviewRequest,
        valueFromResponsible: res.valueFromResponsible,
        valueToTeacher: res.valueToTeacher,
        valueToTrip: res.valueToTrip,
        wasStudentAbsent: res.wasStudentAbsent,
      });
      setReportInfoOpen(true);
    });
  };

  const handleCancelPayment = async (paymentId: number) => {
    setIsLoadingCancel(true);
    await apiService
      .cancelPayment(paymentId)
      .then((updatedPayment) => {
        onUpdate?.(updatedPayment);
        toast.success('Cobrança cancelada com sucesso!');
      })
      .finally(() => {
        setIsLoadingCancel(false);
        setOpenCancelModal(false);
      });
  };

  const handleRestorePayment = async (paymentId: number) => {
    setIsLoadingRestore(true);
    await apiService
      .restorePayment(paymentId)
      .then((updatedPayment) => {
        onUpdate?.(updatedPayment);
        toast.success('Cobrança restaurada com sucesso!');
      })
      .finally(() => {
        setIsLoadingRestore(false);
      });
  };

  const handleMarkAsReceivedInCash = async (paymentId: number) => {
    setIsLoadingReceiveInCash(true);
    await apiService
      .markAsReceivedInCash(paymentId)
      .then((res) => {
        onUpdate?.(res);
        toast.success('Pagamento confirmado com sucesso');
      })
      .finally(() => {
        setIsLoadingReceiveInCash(false);
        setOpenReceiveInCashModal(false);
      });
  };

  const formatPaidValue = (value: number, paidValue: number): string => {
    if (!paidValue || paidValue === value) {
      return '';
    }

    return ` (${paidValue > value ? '+' : '-'}${formatMoney(
      paidValue - value
    )})`;
  };

  const seeInvoiceLabel = payment.invoiceUrl && !payment.bankSlipUrl;

  const onClickBilletButton = () => {
    if (!(payment.bankSlipUrl || payment.invoiceUrl)) {
      toast.warning(
        'Boleto foi excluido por ter seu valor abatido com créditos'
      );
    }
  };

  return (
    <>
      <DraggableModal
        title={'Detalhes do pagamento'}
        open={open}
        onClose={onClose}
      >
        <div className="flex flex-col gap-0">
          <p>
            <span>Responsável:</span>
            <b className="ml-2">{payment.payerName}</b>
          </p>

          <p
            title={
              payment.paidValue
                ? 'Valor inicial estipulado a ser pago'
                : undefined
            }
          >
            <span>Valor:</span>
            <b className="ml-2">
              {formatMoney(payment.value - payment.discountValue)}
            </b>
          </p>
          {payment.paidValue && payment.paidValue !== payment.value && (
            <p title="Valor pago quando há acréscimos ao valor inicial">
              <span>Valor pago:</span>
              <b className="ml-2">
                {formatMoney(payment.paidValue)}{' '}
                {formatPaidValue(payment.value, payment.paidValue)}
              </b>
            </p>
          )}
          {payment.discountValue !== 0 && (
            <>
              <p title="Valor original sem o desconto de créditos">
                <span>Valor original:</span>
                <b className="ml-2">{formatMoney(payment.value)}</b>
              </p>
              <p title="Valor descontado de créditos para ser usado no pagamento do boleto">
                <span>Valor descontado de créditos:</span>
                <b className="ml-2">{formatMoney(payment.discountValue)}</b>
              </p>
            </>
          )}

          <div className="flex items-center gap-2">
            <p>Status:</p>
            <div>
              <StatusLabel status={translatePaymentStatus(payment)} />
            </div>
          </div>

          {isPaid && (
            <>
              {payment.paymentDate && (
                <p>
                  <span>Data de pagamento:</span>
                  <b className="ml-2">
                    {dayjs(payment.paymentDate).format('DD/MM/YYYY')}
                  </b>
                </p>
              )}
              {payment.method && (
                <p>
                  <span>Forma de pagamento:</span>
                  <b className="ml-2">
                    {translatePaymentMethod(payment.method)}
                  </b>
                </p>
              )}
            </>
          )}
          <p>
            <span>Motivo:</span>
            <b className="ml-2">{translatePaymentReason(payment.reason)}</b>
          </p>
          {payment.reason === PaymentReason.ADD_USER_CREDITS &&
            payment.description && (
              <p>
                <span>Descrição:</span>
                <b className="ml-2">{payment.description}</b>
              </p>
            )}
          <p>
            <span>Vencimento:</span>
            <b className="ml-2">
              {dayjs(payment.dueDate).format('DD/MM/YYYY')}
            </b>
          </p>
        </div>
        <div>
          {payment.reason === PaymentReason.PAY_FOR_CLASSES && (
            <>
              <h4>Relatórios das aulas incluídas na cobrança</h4>
              <ul className="flex flex-col list-none px-1">
                {isPaymentsDataLoading && (
                  <li className="flex justify-center items-center">
                    <CircularProgress size="1.66rem" color="secondary" />
                  </li>
                )}
                <ReportsInfoList
                  reports={reportsDetails}
                  onOpenReport={handleOpenReport}
                />
              </ul>
            </>
          )}
        </div>
        <div className="flex flex-col items-center flex-wrap gap-4">
          <div className="flex justify-center flex-wrap gap-4">
            {isAdminOrEmployee && payment.status === PaymentStatus.PENDING && (
              <>
                <Button
                  title="Pago em dinheiro"
                  circular
                  startIcon={<LocalAtm />}
                  fullWidth={false}
                  onClick={() => setOpenReceiveInCashModal(true)}
                  isLoading={isLoadingReceiveInCash}
                >
                  Pago em dinheiro
                </Button>

                <Button
                  title="Editar pagamento"
                  circular
                  startIcon={<Edit />}
                  fullWidth={false}
                  onClick={() => setOpenUpdatePaymentModal(true)}
                >
                  Editar pagamento
                </Button>
              </>
            )}

            {payment.status !== PaymentStatus.CANCELED && (
              <Button
                title="Visualizar"
                circular
                startIcon={<LaunchRounded />}
                fullWidth={false}
                href={
                  seeInvoiceLabel ? payment.invoiceUrl : payment.bankSlipUrl
                }
                target="_blank"
                color="secondary"
                onClick={onClickBilletButton}
              >
                {seeInvoiceLabel ? 'Ver Fatura' : 'Ver Boleto'}
              </Button>
            )}
          </div>

          <div className="flex justify-center flex-wrap gap-4">
            {showCancelButton && (
              <Button
                title="Cancelar cobrança"
                circular
                startIcon={<CancelIcon />}
                fullWidth={false}
                onClick={() => setOpenCancelModal(true)}
                isLoading={isLoadingCancel}
              >
                Cancelar
              </Button>
            )}

            {showRestoreButton && (
              <Button
                title="Restaurar cobrança"
                circular
                startIcon={<RestartAltIcon />}
                fullWidth={false}
                onClick={() => handleRestorePayment(payment.id)}
                isLoading={isLoadingRestore}
              >
                Restaurar
              </Button>
            )}
          </div>
        </div>
      </DraggableModal>

      {reportInfoOpen && (
        <TeacherReport
          reportInfo={reportInfo}
          onClose={() => {
            setReportInfo(undefined);
            setReportInfoOpen(false);
          }}
          title="Relatório"
          open={reportInfoOpen}
          hideButtons={hideReportsButons}
        />
      )}

      {/* Cancel */}
      <ConfirmationModal
        onAccept={() => handleCancelPayment(payment.id)}
        onDecline={() => setOpenCancelModal(false)}
        open={openCancelModal}
        isLoading={isLoadingCancel}
        title="Cancelar cobrança"
        description="Deseja realmente cancelar a cobrança?"
      />

      {/* Received in cash */}
      <ConfirmationModal
        onAccept={() => handleMarkAsReceivedInCash(payment.id)}
        onDecline={() => setOpenReceiveInCashModal(false)}
        open={openReceiveInCashModal}
        isLoading={isLoadingReceiveInCash}
        title="Confirmar Recebimento em Dinheiro"
        description="Deseja realmente marcar o pagamento como recebido em dinheiro?"
      />

      {/* Update payment */}
      <UpdatePaymentModal
        open={openUpdatePaymentModal}
        onClose={() => setOpenUpdatePaymentModal(false)}
        paymentData={payment}
        onUpdatedPayment={(updatedPayment) => {
          onUpdate?.(updatedPayment);
        }}
      />
    </>
  );
};
